// src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from 'react-query';
import { msalConfig } from './auth/authConfig';
import { googleConfig } from './auth/googleConfig';
import TestModeApp from './App.test-mode';
import AuthWrapper from './components/auth/AuthWrapper';
import './index.css';

// Load test utilities in development
if (process.env.NODE_ENV === 'development') {
  import('./api/test-proxy').then(() => {
    console.log('\n');
    console.log('%c💡 Type testProxy() in console to test proxy configuration', 'background: #dbeafe; color: #1e40af; padding: 6px 12px; border-radius: 4px; font-weight: bold;');
    console.log('\n');
  });

  import('./api/test-all-apis').then(() => {
    console.log('%c💡 Type testAllApis() in console to test all API endpoints', 'background: #dbeafe; color: #1e40af; padding: 6px 12px; border-radius: 4px; font-weight: bold;');
    console.log('\n');
  });
}

// Check if bypass auth mode is enabled
const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === 'true';

// Log authentication mode in development
if (process.env.NODE_ENV === 'development') {
  console.log('%c🔐 Authentication Mode', 'background: #3b82f6; color: white; padding: 4px 8px; font-weight: bold');
  console.log('Bypass Auth:', bypassAuth);
  console.log('Client ID:', process.env.REACT_APP_CLIENT_ID);
  console.log('Tenant ID:', process.env.REACT_APP_TENANT_ID);
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Set active account if available (only if valid)
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0]);
  if (process.env.NODE_ENV === 'development') {
    console.log('%c✅ Active account found:', 'color: #10b981; font-weight: bold', accounts[0].username);
  }
} else {
  if (process.env.NODE_ENV === 'development') {
    console.log('%c⚠️ No active account found - User will need to login', 'color: #f59e0b; font-weight: bold');
  }
}

// Register event callbacks for login/logout
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account;
    msalInstance.setActiveAccount(account);
    localStorage.setItem('isAuthenticated', 'true');
  }
  if (event.eventType === EventType.LOGOUT_SUCCESS) {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    sessionStorage.clear();
  }
});

// Loading component shown during authentication
const InProgressComponent = ({ inProgress }) => {
  return (
    <div className="w-screen h-screen flex flex-col items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      <p className="mt-4 text-indigo-600 font-semibold">{inProgress} In Progress...</p>
    </div>
  );
};

// Error component shown if authentication fails
const ErrorComponent = ({ error }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Error</h2>
        <p className="text-gray-600 mb-4">
          This is a protected page and the following error occurred during authentication:
        </p>
        <p className="text-red-600 font-medium mb-6">
          {error.errorCode}: {error.errorMessage}
        </p>
        <button
          onClick={() => {
            // Store current URL for return after login
            const currentUrl = window.location.href;
            sessionStorage.setItem('returnUrl', currentUrl);

            // Clear any existing session data
            sessionStorage.clear();
            localStorage.clear();

            // Reload the page to restart authentication
            window.location.reload();
          }}
          className="bg-indigo-600 text-white py-2 px-6 rounded-lg hover:bg-indigo-700 font-semibold transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

// Render the application
const root = ReactDOM.createRoot(document.getElementById('root'));

// If bypass auth is enabled, render test mode app
if (bypassAuth) {
  console.log('%cDevelopment Mode: Azure AD authentication bypassed', 'color: #f59e0b;');

  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <TestModeApp />
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
} else {
  // Normal authentication flow with login page
  const handleGoogleSuccess = (credentialResponse) => {
    console.log('Google login success - storing credentials');
    // Store Google auth info
    localStorage.setItem('googleCredential', credentialResponse.credential);
    localStorage.setItem('authProvider', 'google');
    localStorage.setItem('isAuthenticated', 'true');
    // Dispatch auth update event
    window.dispatchEvent(new Event('authUpdate'));
    // Reload to apply authentication
    window.location.reload();
  };

  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <GoogleOAuthProvider clientId={googleConfig.clientId}>
            <MsalProvider instance={msalInstance}>
              <AuthWrapper onGoogleSuccess={handleGoogleSuccess} />
            </MsalProvider>
          </GoogleOAuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
}